import { TGetProvincesAndMunicipalitiesResDto } from "@/api/services/global/constant/dtos/get-provinces-municipalities.dto";
import { useFetchPrefectureAndMunicipalityByZipCode } from "@/api/services/global/zip-code";
import { INVALID_NOT_EMPTY, INVALID_ZIP_CODE } from "@/constants/invalids";
import { useCacheLocal } from "@/hooks/useCacheLocal";
import { convertZipCodeToNumber } from "@/utils/input";
import { convertFullWidthToHalfWidth } from "@/utils/pieces";
import { useEffect, useRef, useState } from "react";
import { FieldValues, Path, PathValue, UseFormReturn, useWatch } from "react-hook-form";

interface ErrorMessage {
  code: string;
  message: string;
}

export type TZipcodeStorage = {
  createdAt: string;
  values: (TGetProvincesAndMunicipalitiesResDto & {
    zipCode?: string;
  })[];
};

export type UseZipCodeHandlerProps<T extends FieldValues> = {
  names: {
    zipCode: Path<T>;
    province: Path<T>;
    municipality?: Path<T>;
    address?: Path<T>;
  };
  methods: UseFormReturn<T>;
  readOnly?: boolean;
};

export function useZipCodeHandler<T extends FieldValues>({ names, methods, readOnly }: UseZipCodeHandlerProps<T>) {
  const { setValue, trigger } = methods;
  const { fetchByZipCode } = useFetchPrefectureAndMunicipalityByZipCode();
  const zipCodeField = useWatch({ control: methods.control, name: names.zipCode });

  const zipCodeStorage = useCacheLocal("zipCode");
  let cachedZipCode: TZipcodeStorage = zipCodeStorage.getJSON();

  const [locationInfo, setLocationInfo] = useState<TGetProvincesAndMunicipalitiesResDto | undefined>(undefined);
  const [isFetching, setIsFetching] = useState(false);
  const [previousZipCode, setPreviousZipCode] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const isFirstTime = useRef(true);

  const dirtyFields = methods.formState.dirtyFields as { [key: string]: boolean };
  const dirtyZipCodeField = dirtyFields[names.zipCode];

  useEffect(() => {
    if (!isFirstTime.current || readOnly) return;
    const zipCode = convertZipCodeToNumber(convertFullWidthToHalfWidth(zipCodeField));

    const found = cachedZipCode?.values?.find((item) => item?.zipCode === zipCode);
    if (zipCode) {
      setPreviousZipCode(zipCode);
      if (zipCode.length === 7 && !found && !readOnly) {
        handleZipCodeChange(zipCode);
      } else if (found) {
        setLocationInfo(found);
      }
    }
    isFirstTime.current = false;
  }, [zipCodeField, readOnly, zipCodeStorage]);

  const updateLocationFields = async (province = "", municipality = "", address = "") => {
    setValue(names.province, province as PathValue<T, Path<T>>, { shouldValidate: true });
    await trigger(names.province);

    if (names.municipality && dirtyZipCodeField) {
      setValue(names.municipality, municipality as PathValue<T, Path<T>>, { shouldValidate: true });
      await trigger(names.municipality);
    }

    if (names.address && dirtyZipCodeField) {
      setValue(names.address, address as PathValue<T, Path<T>>, { shouldValidate: true });
      await trigger(names.address);
    }
  };
  const fetchLocationInfo = async (zipCode: string) => {
    setIsFetching(true);
    try {
      const found = cachedZipCode?.values?.find((item) => item?.zipCode === zipCode);
      if (found) {
        setLocationInfo(found);
        await updateLocationFields(found.prefecture, found.city, found?.suburb);
        return;
      }
      const rs = await fetchByZipCode(zipCode);
      setLocationInfo(rs);
      if (rs) {
        cachedZipCode?.values.push({ ...rs, zipCode });
        zipCodeStorage.setJSON(cachedZipCode);
        await updateLocationFields(rs.prefecture, rs.city, rs?.suburb);
        const hasAddressError = names.address && !!methods.formState.errors[names.address];
        const hasProvinceError = names.province && !!methods.formState.errors[names.province];
        const hasMunicipalityError = names.municipality && !!methods.formState.errors[names.municipality];
        if (hasAddressError || hasProvinceError || hasMunicipalityError) {
          return INVALID_NOT_EMPTY;
        }
      }
    } catch (err) {
      const errorMessage = (err as ErrorMessage)?.message || INVALID_ZIP_CODE;
      await updateLocationFields();
      return errorMessage;
    } finally {
      setIsFetching(false);
    }
  };

  const isOlderThan3Days = (createdAt: string): boolean => {
    const createdTime = new Date(createdAt).getTime();
    const now = Date.now();

    const diffInMs = now - createdTime;
    const threeDaysInMs = 3 * 24 * 60 * 60 * 1000;

    return diffInMs > threeDaysInMs;
  };

  const handleZipCodeChange = async (value: string): Promise<string | null> => {
    const zipCode = convertZipCodeToNumber(convertFullWidthToHalfWidth(value));

    if (!zipCode || zipCode.length === 0) {
      setPreviousZipCode(undefined);
      updateLocationFields();
      return null;
    }
    if (!cachedZipCode?.createdAt) {
      cachedZipCode = { createdAt: "", values: [] };
    }
    if (!cachedZipCode?.createdAt || isOlderThan3Days(cachedZipCode?.createdAt)) {
      cachedZipCode.createdAt = new Date().toISOString();
      if (isOlderThan3Days(cachedZipCode.createdAt)) {
        cachedZipCode.values = [];
      }
      zipCodeStorage.setJSON(cachedZipCode);
    }
    if (zipCode.length === 7 && zipCode !== previousZipCode) {
      const error = await fetchLocationInfo(zipCode);
      setPreviousZipCode(zipCode);
      setErrorMessage(error);
      if (error && error !== INVALID_NOT_EMPTY) {
        methods.setError(names.zipCode, { message: error });
      } else {
        methods.clearErrors(names.zipCode);
      }
      return error || null;
    } else if (errorMessage === INVALID_NOT_EMPTY) {
      await trigger(names.province);
      if (names.municipality) {
        await trigger(names.municipality);
      }
      if (names.address) {
        await trigger(names.address);
      }
      const hasAddressError = names.address && !!methods.formState.errors[names.address];
      const hasProvinceError = names.province && !!methods.formState.errors[names.province];
      const hasMunicipalityError = names.municipality && !!methods.formState.errors[names.municipality];
      if (hasAddressError || hasProvinceError || hasMunicipalityError) {
        setErrorMessage(INVALID_NOT_EMPTY);
        return INVALID_NOT_EMPTY;
      }
      setErrorMessage(undefined);
      return null;
    }
    return errorMessage ?? null;
  };

  return { handleZipCodeChange, locationInfo, isFetching };
}
